import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Keycloak from 'keycloak-js';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CalendarPage from './pages/CalendarPage';
import TasksPage from './pages/TasksPage';
import ConfigPage from './pages/ConfigPage';
import CallFlowPage from './pages/CallFlowPage';
import CallMonitorPage from './pages/CallMonitorPage';
import ChatPage from './pages/ChatPage';
import AdminPracticesPage from './pages/AdminPracticesPage';
import EmailVerificationResult from './pages/EmailVerificationResult';

// Auth Context
import { AuthProvider } from './auth/AuthContext';

// Axios interceptor (Session 5 Lesson 4 — auto-injects Bearer token)
import { setKeycloak as setAxiosKeycloak } from './utils/axiosInstance';

// Call State Context (global polling for active calls)
import { CallStateProvider } from './context/CallStateContext';
import TaskAlerts from './hooks/useTaskAlerts';
import SupportProvider from './components/support/SupportProvider';
import SupportWindow from './components/support/SupportWindow';

// Practice Context (practiceId + logo caching across navigation)
import { PracticeProvider, usePractice } from './context/PracticeContext';

// CSS
import './App.css';

// Check if we should bypass auth (development mode)
const bypassAuth = false; // Set to false to use real Keycloak authentication

// Create a Keycloak instance immediately
const keycloakConfig = {
  url: process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8081',
  realm: process.env.REACT_APP_KEYCLOAK_REALM || 'deskbot',
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'deskbot-frontend',
};

const kc = new Keycloak(keycloakConfig);

// Create a mock keycloak object for development
const mockKeycloak = {
  authenticated: true,
  login: () => {
    console.log('Mock login called - clearing logout flag and reloading');
    localStorage.removeItem('mock_logged_out');
    window.location.reload();
  },
  logout: () => {
    console.log('Mock logout called');
    // Set localStorage flag to indicate logout and force page reload
    localStorage.setItem('mock_logged_out', 'true');
    window.location.href = '/login';
  },
  tokenParsed: {
    sub: 'test-user',
    preferred_username: 'Test User',
    email: 'test@example.com'
  }
};

const App = () => {
  const hasLoggedOut = localStorage.getItem('mock_logged_out') === 'true';
  const isDevBypassActive = bypassAuth && !hasLoggedOut;

  // Initialize state - keycloak starts as the real instance
  const [keycloak, setKeycloak] = useState(kc); 
  const [authenticated, setAuthenticated] = useState(isDevBypassActive);
  const [loading, setLoading] = useState(true); // Start loading true
  
  const initDone = useRef(false);

  // ── Token lifecycle: keep the session alive, recover gracefully ───────────
  // A token refresh failure forces a re-login ONLY when the token is genuinely
  // unusable; a transient network blip is retried, never bounced. When a
  // re-login is unavoidable it returns the user to the exact page they were on,
  // and if the Keycloak SSO session is still valid it happens with no prompt.
  // So the common cases (sleep, idle, the nightly backend restart) heal
  // invisibly and an actual interactive logout stays rare.
  const recoveringRef = useRef(false);

  const reauthenticate = useCallback(() => {
    if (recoveringRef.current) return; // guard against redirect loops / double-fire
    recoveringRef.current = true;
    console.warn('[AUTH] Session expired — re-authenticating, returning to current page');
    kc.login({ redirectUri: window.location.href });
  }, []);

  const tryRefreshToken = useCallback(() => {
    if (bypassAuth) return;
    kc.updateToken(70).catch(() => {
      // Re-auth only when the access token is truly dead; otherwise it's a
      // transient failure that the next tick retries — so a flaky network never
      // triggers a spurious redirect.
      if (typeof kc.isTokenExpired === 'function' && kc.isTokenExpired()) {
        reauthenticate();
      } else {
        console.warn('[AUTH] Token refresh failed — will retry');
      }
    });
  }, [reauthenticate]);

  useEffect(() => {
    if (!initDone.current) {
      initDone.current = true;
      console.log('Initializing authentication...');

      if (bypassAuth) {
        // Always use mock in development mode, just check for logout flag
        console.log('Development mode: using mock authentication');
        const currentlyLoggedOut = localStorage.getItem('mock_logged_out') === 'true';
        setAuthenticated(!currentlyLoggedOut);
        setKeycloak(!currentlyLoggedOut ? mockKeycloak : kc);
        setAxiosKeycloak(!currentlyLoggedOut ? mockKeycloak : kc);
        setLoading(false);
      } else {
        // Production mode: use real Keycloak
        console.log('Production mode: initializing Keycloak');
        kc.init({
          onLoad: 'login-required',
          silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
          pkceMethod: 'S256',
          checkLoginIframe: false,
        })
        .then((auth) => {
          console.log(`Keycloak init finished. Auth status: ${auth}`);
          setAuthenticated(auth);
          setKeycloak(kc);
          setAxiosKeycloak(kc);
          setLoading(false);
          // Token refresh + on-focus recovery are wired in the dedicated effect
          // below (keyed on `authenticated`).
        })
        .catch((error) => {
          console.error('Keycloak init error:', error);
          setAuthenticated(false);
          setKeycloak(kc);
          setLoading(false);
        });
      }
    }
  }, []); // Only run once on mount

  // Keep the token alive while authenticated: a 60s heartbeat PLUS an on-focus
  // refresh. Timers are throttled in background tabs and frozen while the laptop
  // sleeps, so focus/visibility is the reliable trigger when the tab comes back
  // (e.g. Monday morning) — without it the session silently lapses and the page
  // shows stale data with no warning.
  useEffect(() => {
    if (bypassAuth || !authenticated) return;

    const intervalId = setInterval(tryRefreshToken, 60000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tryRefreshToken();
    };
    window.addEventListener('focus', tryRefreshToken);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', tryRefreshToken);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [authenticated, tryRefreshToken]);

  if (loading) {
    return <div className="loading-container">
      <div className="spinner"></div>
      <p>Loading DeskBot...</p>
    </div>;
  }

  // Use a Protected Route component to handle authentication
  const ProtectedRoute = ({ children }) => {
    const { practiceRole, loading, isUserSuperAdmin } = usePractice();
    const location = useLocation();
    // Use the current 'authenticated' state for protection
    if (!authenticated) {
      return <Navigate to="/login" />;
    }
    // task_manager (per-practice role) may see ONLY the Tasks page. /tasks always
    // renders; every other route waits for the per-practice role to load (so we
    // never flash a page then bounce) and redirects a task_manager to /tasks.
    // super_admin is never restricted. Backend enforces the same on the API.
    if (location.pathname !== '/tasks') {
      if (loading) {
        return null;
      }
      if (practiceRole === 'task_manager' && !isUserSuperAdmin) {
        return <Navigate to="/tasks" replace />;
      }
    }
    return children;
  };

  // Admin-only route - requires authentication AND admin role
  const AdminRoute = ({ children }) => {
    const { isUserAdmin } = usePractice();
    if (!authenticated) {
      return <Navigate to="/login" />;
    }
    if (!isUserAdmin) {
      return <Navigate to="/dashboard" />;
    }
    return children;
  };

  // Session 10: SuperAdmin-only route for the Praxisverwaltung page
  // (admin_practices.router endpoints) and the Kalender.
  const SuperAdminRoute = ({ children }) => {
    const { isUserSuperAdmin } = usePractice();
    if (!authenticated) {
      return <Navigate to="/login" />;
    }
    if (!isUserSuperAdmin) {
      return <Navigate to="/dashboard" />;
    }
    return children;
  };

  // Provide the current keycloak state (real or mock) and authenticated status
  const authProviderValue = { keycloak, authenticated };

  return (
    <AuthProvider value={authProviderValue}>
      <PracticeProvider>
        <CallStateProvider>
          <TaskAlerts />
          {/* Wraps only the window: it is keyed by practice (see SupportProvider). */}
          <SupportProvider>
            <SupportWindow />
          </SupportProvider>
          <div className="app">
            <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Public, no-auth landing pages for the SES email-verification redirect. */}
            <Route path="/email-bestaetigt" element={<EmailVerificationResult status="success" />} />
            <Route path="/email-fehlgeschlagen" element={<EmailVerificationResult status="failure" />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <SuperAdminRoute>
                <CalendarPage />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <TasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/config"
            element={
              <ProtectedRoute>
                <ConfigPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/call-flow"
            element={
              <ProtectedRoute>
                <CallFlowPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/call-monitor"
            element={
              <ProtectedRoute>
                <CallMonitorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <AdminRoute>
                <ChatPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/practices"
            element={
              <SuperAdminRoute>
                <AdminPracticesPage />
              </SuperAdminRoute>
            }
          />
          {/* Unknown paths → home (a task_manager is bounced on to /tasks by ProtectedRoute). */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        </CallStateProvider>
      </PracticeProvider>
    </AuthProvider>
  );
};

export default App; 